// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dataset selector for analysis nodes.
 * Selects from dataset definitions configured on the workspace.
 */
import type { FC } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Database } from 'lucide-react'
import type { DatasetDefinition } from '@/subsystems/pipelines/types'

interface DatasetOutputSectionProps {
  datasetKey: string
  datasets: DatasetDefinition[]
  onDatasetKeyChange: (key: string) => void
}

export const DatasetOutputSection: FC<DatasetOutputSectionProps> = ({
  datasetKey,
  datasets,
  onDatasetKeyChange,
}) => {
  const selectedDataset = datasets.find(d => d.key === datasetKey)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          <CardTitle>Dataset</CardTitle>
        </div>
        <CardDescription>
          Select the dataset definition for this node. The dataset determines the schema,
          knowledge base, and system prompt.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {datasets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No datasets configured for this workspace. Add datasets in the Datasets tab of the builder.
          </p>
        ) : (
          <Select value={datasetKey} onValueChange={onDatasetKeyChange}>
            <SelectTrigger data-testid="analysis-dataset-select">
              <SelectValue placeholder="Select a dataset" />
            </SelectTrigger>
            <SelectContent>
              {datasets.map(ds => (
                <SelectItem key={ds.key} value={ds.key}>
                  {ds.label} ({ds.key})
                </SelectItem>
              ))}
              {datasetKey && !datasets.some(d => d.key === datasetKey) && (
                <SelectItem value={datasetKey}>
                  {datasetKey} (missing)
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}

        {selectedDataset && (
          <div className="rounded-md border bg-muted/50 p-3 space-y-1 text-xs text-muted-foreground">
            {selectedDataset.schemaId && <div>Schema: <span className="font-mono">{selectedDataset.schemaId}</span></div>}
            {selectedDataset.knowledgeBase && <div>Knowledge Base: <span className="font-mono">{selectedDataset.knowledgeBase}</span></div>}
            {selectedDataset.systemPrompt && <div>System prompt configured</div>}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
